import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Close as CloseIcon } from '@mui/icons-material';
import cx from 'classnames';
import { showErrorToast } from '@shared/services/toastService';
import UploadButton from '../buttons/UploadButton';

/**
 * @typedef {object} FormInputFileOwnProps
 * @property {string} name
 * @property {Array<string>} acceptedFileTypes
 * @property {(selectedFile: File) => { message: string } | undefined} validateFile
 * @property {() => void} [onRemoveFile]
 * @property {boolean} [hideRemoveButton]
 * @property {string} [label]
 */

/**
 * @typedef {FormInputFileOwnProps & React.ComponentProps<typeof UploadButton>} FormInputFileProps
 */

/**
 * @param {FormInputFileProps} props
 */
const FormInputFile = props => {
  const {
    name,
    acceptedFileTypes,
    validateFile,
    onRemoveFile = () => {},
    hideRemoveButton,
    label = 'Upload',
    ...otherProps
  } = props;
  const {
    control,
    formState: { errors },
    setValue
  } = useFormContext();

  const normalizedAcceptedFileTypes = acceptedFileTypes.map(type => `.${type}`);

  const handleFileChange = e => {
    const selectedFile = e.target.files[0];

    const error = validateFile(selectedFile);
    if (error) {
      showErrorToast(error.message);
      return;
    }

    setValue(name, selectedFile);
  };

  const handleFileRemove = () => {
    setValue(name, null);
    onRemoveFile();
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <div className='input-file-upload-container'>
          {!field.value && (
            <UploadButton
              className={cx('upload-button', { error: !!errors[name] })}
              label={label}
              acceptedFileTypes={normalizedAcceptedFileTypes}
              handleFileChange={handleFileChange}
              {...otherProps}
            />
          )}

          {field.value && (
            <div className='file-uploaded'>
              <span>{field.value.name}</span>
              {!hideRemoveButton && (
                <CloseIcon
                  className='remove-document-icon'
                  data-testid='remove-document'
                  onClick={handleFileRemove}
                />
              )}
            </div>
          )}
        </div>
      )}
    />
  );
};

export default FormInputFile;
